package ai.wagemix.components.layouts

import ai.wagemix.SiteLogo
import ai.wagemix.analytics.WMCta
import ai.wagemix.analytics.WMCtaLocation
import ai.wagemix.analytics.sendWMCtaClicked
import ai.wagemix.ThemeToggle
import ai.wagemix.di.koin
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMRoutes
import ai.wagemix.navigate
import ai.wagemix.siteStrings
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMFooterColumn
import ai.wagemix.web.components.WMMarketingFooter
import ai.wagemix.web.components.WMMarketingHeader
import ai.wagemix.web.components.WMNavItem
import ai.wagemix.web.screens.marketing.WMDefaultMarketingChromeWebViewModel
import ai.wagemix.web.screens.marketing.WMMarketingChromeScreen
import ai.wagemix.web.theme.WMContentWidthVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.web.screens.FSPageScaffold
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.px
import org.koin.core.parameter.parametersOf

/**
 * Penpot `Marketing Header` + `Marketing Footer` around the logged-out pages. The header's session
 * actions come from the marketing chrome presenter; on the landing page it also sends a signed-in
 * visitor on to the mix builder (client-side, so the exported page stays as it is).
 */
@Layout
@Composable
fun MarketingLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val path = ctx.route.path
    val leaveWhenSignedIn = path == WMRoutes.HOME
    WMMarketingChromeScreen(
        createViewModel = {
            koin.get<WMDefaultMarketingChromeWebViewModel> {
                parametersOf(leaveWhenSignedIn, { request: WMNavigationRequest -> ctx.navigate(request) })
            }
        },
        leaveWhenSignedIn = leaveWhenSignedIn,
        strings = siteStrings,
        onSignIn = { ctx.router.navigateTo(WMRoutes.LOGIN) },
        onRegister = {
            FSLog.sendWMCtaClicked(cta = WMCta.CreateAccount, location = WMCtaLocation.Header)
            ctx.router.navigateTo(WMRoutes.REGISTER)
        },
        onOpenAppClick = { FSLog.sendWMCtaClicked(cta = WMCta.OpenApp, location = WMCtaLocation.Header) }
    ) { sessionActions ->
        MarketingChrome(ctx, sessionActions, content)
    }
}

@Composable
private fun MarketingChrome(ctx: PageContext, sessionActions: @Composable () -> Unit, content: @Composable () -> Unit) {
    val s = siteStrings
    val path = ctx.route.path
    var menuOpen by remember { mutableStateOf(false) }
    val items = listOf(
        WMNavItem(s.shell.mixBuilder, WMRoutes.MIX, active = path.startsWith(WMRoutes.MIX)),
        WMNavItem(s.pricing.title, WMRoutes.PRICING, active = path.startsWith(WMRoutes.PRICING)),
        WMNavItem(s.shell.methodology, "/methodology", active = path.startsWith("/methodology"))
    )
    FSPageScaffold(
        contentMaxWidth = WMContentWidthVar.value(),
        header = {
            WMMarketingHeader(
                logo = { SiteLogo() },
                items = items,
                menuLabel = s.common.menu,
                onMenuClick = { menuOpen = !menuOpen }
            ) {
                ThemeToggle()
                sessionActions()
            }
            if (menuOpen) {
                Column(Modifier.fillMaxWidth().padding(16.px).gap(8.px).displayUntil(Breakpoint.MD)) {
                    items.forEach { item ->
                        WMButton(item.label, kind = WMButtonKind.Ghost, fullWidth = true) { menuOpen = false; ctx.router.navigateTo(item.path) }
                    }
                }
            }
        },
        footer = {
            WMMarketingFooter(
                logo = { SiteLogo() },
                tagline = s.auth.brandTitle,
                columns = listOf(
                    WMFooterColumn("Product", listOf(
                        WMNavItem(s.shell.mixBuilder, WMRoutes.MIX),
                        WMNavItem(s.pricing.title, WMRoutes.PRICING),
                        WMNavItem(s.shell.methodology, "/methodology")
                    )),
                    WMFooterColumn("Legal", listOf(
                        WMNavItem("Terms", "/terms"),
                        WMNavItem("Privacy", "/privacy-policy")
                    ))
                ),
                copyright = "© 2026 Wagemix",
                dataSource = s.common.dataSource
            )
        }
    ) {
        content()
    }
}
